import {
  getAllRecords,
  getRecord,
  insertRecord,
  updateRecord,
} from "@/utils/helpers/data";
import { POSTGREST_PAGE_SIZE, TABLES } from "@/utils/constants";
import { Gender } from "@/utils/types";
import { supabase } from "@/utils/supabase";
import { PostgrestError } from "@supabase/supabase-js";

export interface Patient {
  id?: string;
  first_name: string | null;
  last_name: string | null;
  mobile_number?: string | null;
  gender?: Gender | null;
  birthday?: string | null;
  created_at?: string;
  updated_at?: string;
  is_user?: boolean;
  dentist_id?: string | null;
}

export const COLUMNS = {
  ID: "id",
  FIRST_NAME: "first_name",
  LAST_NAME: "last_name",
  MOBILE_NUMBER: "mobile_number",
  GENDER: "gender",
  BIRTHDAY: "birthday",
  CREATED_AT: "created_at",
  UPDATED_AT: "updated_at",
  IS_USER: "is_user",
  DENTIST_ID: "dentist_id",
};

/**
 * Creates a new patient record in the database.
 * @param patient - The patient data to insert.
 * @returns The created patient or null if insertion failed.
 * @throws Will throw an error if the insertion fails.
 */
export const createPatient = async (
  patient: Patient,
): Promise<Patient | null> => {
  return insertRecord<Patient>(TABLES.PATIENTS, patient);
};

/**
 * Updates an existing patient record in the database.
 * @param patientId - The ID of the patient to update.
 * @param patient - The updated patient data.
 * @returns The updated patient or null if update failed.
 * @throws Will throw an error if the update fails.
 */
export const updatePatient = async ({
  patientId,
  patient,
}: {
  patientId: string | null | undefined;
  patient: Patient;
}): Promise<Patient | null> => {
  if (!patientId) return null;
  return updateRecord<Patient>(TABLES.PATIENTS, COLUMNS.ID, patientId, patient);
};

/**
 * Retrieves a single patient by their ID.
 * @param patientId - The ID of the patient to retrieve.
 * @returns The requested patient or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getPatient = async (
  patientId: string | null | undefined,
): Promise<Patient | null> => {
  if (!patientId) return null;
  return getRecord<Patient>(TABLES.PATIENTS, COLUMNS.ID, patientId);
};

/**
 * Retrieves all patients from the database.
 * @returns An array of all patients.
 * @throws Will throw an error if the retrieval fails.
 */
export const getAllPatients = async (): Promise<Patient[]> => {
  return getAllRecords<Patient>(TABLES.PATIENTS);
};

/**
 * Retrieves all patients associated with a specific dentist.
 * Paginates through all requests to ensure all patients are retrieved, using count to determine iterations.
 * @param dentistId - The ID of the dentist to filter patients by.
 * @returns An array of patients associated with the dentist.
 * @throws Will throw an error if the retrieval fails.
 */
export const getPatientsByDentistId = async (
  dentistId: string | null | undefined,
): Promise<Patient[]> => {
  if (!dentistId) return [];

  const PAGE_SIZE = POSTGREST_PAGE_SIZE;

  // First, get the count of matching requests
  const countResult = await supabase
    .from(TABLES.REQUESTS)
    .select("*", { count: "exact", head: true })
    .eq(COLUMNS.DENTIST_ID, dentistId);

  if (countResult.error) {
    throw new Error(countResult.error.message);
  }

  const totalCount = countResult.count ?? 0;
  if (totalCount === 0) return [];

  const pageCount = Math.ceil(totalCount / PAGE_SIZE);
  let allRequests: { patient: Patient }[] = [];

  for (let page = 0; page < pageCount; page++) {
    const { data: requests, error: requestsError } = await supabase
      .from(TABLES.REQUESTS)
      .select("patient:patient_id(*)")
      .eq(COLUMNS.DENTIST_ID, dentistId)
      .order("id", { ascending: true })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1) as {
        data: { patient: Patient }[] | null;
        error: PostgrestError;
      };

    if (requestsError) {
      throw new Error(requestsError.message);
    }

    allRequests = allRequests.concat(requests ?? []);
  }

  return allRequests.map((request) => request.patient);
};
